import { SignedOut } from '@clerk/clerk-react';
import { type ReactElement, useCallback } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { s } from '@/theme/tokens';
import { fontFamilies } from '@/theme/tokens/typography';
import { Box, Inline, Text } from '@/ui';
import { BreadcrumbsProvider } from '@/ui/layout/BreadcrumbsProvider';
import { PublicShell } from '@/ui/layout/PublicShell';
import { Button } from '@/ui/primitives/Button';
import { AuthenticatedLayout } from '../layouts/AuthenticatedLayout';
import { shouldShowPublicAuthActions } from './appShellRouterUtils';
import type { AppBootstrapControllerState } from './AppBootstrapController';
import type { AuthError, AuthSession } from '@/auth/types';
import type { NavItem } from '@/components/sidebarTypes';
import type { OrganisationDto, ProjectDto } from '@flowqa/shared';

type AppShellRouterProps = {
  state: AppBootstrapControllerState;
  session: AuthSession | null;
  authError: AuthError | null;
  isAdmin: boolean;
  isSuperuser: boolean;
  navItems: NavItem[];
  projects: Array<Pick<ProjectDto, 'id' | 'name' | 'slug'>>;
  allProjects: Array<Pick<ProjectDto, 'id' | 'name' | 'slug'>>;
  organisations: Array<Pick<OrganisationDto, 'id' | 'name' | 'slug'>>;
  canCreateProject: boolean;
  planName: string | null;
  lockProjectSelector: boolean;
  onAddProject: () => void;
  onAddOrganisation: () => void;
  onSignOut: () => void;
};

export const AppShellRouter = ({
  state,
  session,
  authError,
  isAdmin,
  isSuperuser,
  navItems,
  projects,
  allProjects,
  organisations,
  canCreateProject,
  planName,
  lockProjectSelector,
  onAddProject,
  onAddOrganisation,
  onSignOut,
}: AppShellRouterProps): ReactElement => {
  const navigate = useNavigate();
  const handleManualSignOut = useCallback((): void => {
    void navigate('/sign-in', { replace: true });
    onSignOut();
  }, [navigate, onSignOut]);

  const titleContent = (
    <Box
      sx={{
        color: 'inherit',
        textDecoration: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        gap: s('sm'),
        paddingY: s('sm'),
      }}
    >
      <Box
        component="img"
        src="/logo.png"
        alt="FlowQA"
        sx={{ height: '36px', width: '36px', display: 'block' }}
      />
      <Text
        role="label"
        component="span"
        sx={{ fontFamily: fontFamilies.mono, fontSize: 24, lineHeight: 1 }}
      >
        [FLOWQA]
      </Text>
    </Box>
  );
  const unauthTitle = (
    <Box component={RouterLink} to="/" sx={{ textDecoration: 'none', color: 'inherit' }}>
      {titleContent}
    </Box>
  );
  const showPublicAuthActions = shouldShowPublicAuthActions(
    state.activePath,
    state.isEntryAuthFlow,
  );

  return (
    <BreadcrumbsProvider items={state.breadcrumbItems} visible={state.showBreadcrumbs}>
      {state.showAuthenticatedShell ? (
        <AuthenticatedLayout
          session={session}
          isAdmin={isAdmin}
          isSuperuser={isSuperuser}
          navItems={navItems}
          sidebarFeatureFlags={state.sidebarFeatureFlags}
          hideSidebar={state.onboardingIncomplete || state.activePath === '/onboarding'}
          showSidebarWhenSignedOut={false}
          forceShowSignOut={Boolean(authError) || (!session && state.showAuthenticatedShell)}
          restrictTopBarToSignOut={state.restrictTopBarToSignOut}
          activePath={state.activePath}
          projects={projects}
          allProjects={allProjects}
          organisations={organisations}
          canCreateProject={canCreateProject}
          planName={planName}
          lockProjectSelector={lockProjectSelector}
          onNavigate={(path) => {
            void navigate(path);
          }}
          onAddProject={onAddProject}
          onAddOrganisation={onAddOrganisation}
          onSignOut={handleManualSignOut}
        >
          {state.routes}
        </AuthenticatedLayout>
      ) : (
        <PublicShell
          title={unauthTitle}
          actions={
            showPublicAuthActions ? (
              <Inline gap="md" align="center" wrap="nowrap" sx={{ width: 'auto', pr: s('sm') }}>
                <SignedOut>
                  <Button
                    component={RouterLink}
                    to="/sign-in"
                    variant="primary"
                    fullWidthOnMobile={false}
                  >
                    Sign In
                  </Button>
                  <Button
                    component={RouterLink}
                    to="/sign-up"
                    variant="secondary"
                    fullWidthOnMobile={false}
                  >
                    Sign Up
                  </Button>
                </SignedOut>
              </Inline>
            ) : null
          }
        >
          {state.routes}
        </PublicShell>
      )}
    </BreadcrumbsProvider>
  );
};
